/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import style from './Death.scss?inline';
import { Box } from '@mui/material';
import {
  DeathKindKeys,
  DeathUnknownTimeKeys,
  DeathYesKeys,
  getDeathTuplesFor,
  REANIMATION_DEATH_RECORD_ELEMENT_KEY,
  ReanimationDeathRecord,
} from '../../../../models/genericElements/reanimationDeath';
import CheckboxList from '../../../customs/checkboxList/CheckboxList';
import { useCSS } from '../../../../provider/CSSProvider';
import { CardId } from '../../../../models/diviCard';
import RadioList from '../../../customs/radioList/RadioList';
import { NodeType } from '../../../../backendModels/report.model';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { InputState } from '../../../../backendModels/general.model';
import { Draft } from 'immer';
import { isGenericRecordDeletable } from '../../../../models/generic';
import { TimePicker } from '../../../customs/timePicker/TimePicker';

const determinationCheckboxItem: [DeathYesKeys, string][] = getDeathTuplesFor([DeathYesKeys.todesfeststellung]);

const determinationRadioItems: [DeathKindKeys, string][] = getDeathTuplesFor([
  DeathKindKeys.todesfeststellungnatuerlich,
  DeathKindKeys.todesfeststellungunklar,
  DeathKindKeys.todesfeststellungnichtnatuerlich,
]);

const timeOfDeathItem: [DeathUnknownTimeKeys, string][] = getDeathTuplesFor([
  DeathUnknownTimeKeys.todeszeitpunktnichtbestimmbar,
]);

interface DeathProps {
  nodeType: NodeType;
}

export default function Death({ nodeType }: DeathProps) {
  useCSS(style);

  const { findRecordOrDefault, adaptRecord } = useContext(ReportsAPIContext);
  const record = findRecordOrDefault(
    'generic',
    nodeType,
    REANIMATION_DEATH_RECORD_ELEMENT_KEY
  ) as ReanimationDeathRecord;

  const timeString =
    record.inputState == InputState.ENTERED ? (record.values.reanimationZusaetzeTodeszeitpunkt?.text ?? '') : '';
  // const timeDayjs = timeString != null ? dayjs(timeString, 'HH:mm') : null;

  const updateDidDie = (newValues: DeathYesKeys[]) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ReanimationDeathRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;
        if (newValues.length > 0) {
          draft.values = {
            ...draft.values,
            reanimationZusaetzeTod: {
              fieldType: 'multiSelect',
              options: newValues,
            },
          };
        } else {
          delete draft.values?.reanimationZusaetzeTod;
          delete draft.values?.reanimationZusaetzeTodArt;
          delete draft.values?.reanimationZusaetzeTodeszeitpunkt;
          delete draft.values?.reanimationZusaetzeTodeszeitpunktUnklar;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      REANIMATION_DEATH_RECORD_ELEMENT_KEY
    );
  };

  const updateKind = (newValue: string | null) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ReanimationDeathRecord>, deleteRecord) => {
        if (newValue != null) {
          draft.inputState = InputState.ENTERED;
          draft.values = {
            ...draft.values,
            reanimationZusaetzeTod: {
              fieldType: 'multiSelect',
              options: [DeathYesKeys.todesfeststellung],
            },
            reanimationZusaetzeTodArt: {
              fieldType: 'singleSelect',
              option: newValue as DeathKindKeys,
            },
          };
        } else {
          delete draft.values?.reanimationZusaetzeTodArt;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      REANIMATION_DEATH_RECORD_ELEMENT_KEY
    );
  };

  const updateTime = (newTime: string) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ReanimationDeathRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;
        if (newTime !== '') {
          delete draft.values?.reanimationZusaetzeTodeszeitpunktUnklar;
          draft.values = {
            ...draft.values,
            reanimationZusaetzeTod: {
              fieldType: 'multiSelect',
              options: [DeathYesKeys.todesfeststellung],
            },
            reanimationZusaetzeTodeszeitpunkt: {
              fieldType: 'text',
              text: newTime,
            },
          };
        } else {
          delete draft.values?.reanimationZusaetzeTodeszeitpunkt;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      REANIMATION_DEATH_RECORD_ELEMENT_KEY
    );
  };

  const updateUnknownTime = (newValues: DeathUnknownTimeKeys[]) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ReanimationDeathRecord>) => {
        draft.inputState = InputState.ENTERED;
        if (newValues.length > 0) {
          delete draft.values?.reanimationZusaetzeTodeszeitpunkt;
          draft.values = {
            ...draft.values,
            reanimationZusaetzeTod: {
              fieldType: 'multiSelect',
              options: [DeathYesKeys.todesfeststellung],
            },
            reanimationZusaetzeTodeszeitpunktUnklar: {
              fieldType: 'multiSelect',
              options: newValues,
            },
          };
        } else {
          delete draft.values?.reanimationZusaetzeTodeszeitpunktUnklar;
        }
      },
      REANIMATION_DEATH_RECORD_ELEMENT_KEY
    );
  };

  return (
    <DiviFieldCard cardType={CardId.Death}>
      <CheckboxList
        className='symptoms-list'
        items={determinationCheckboxItem}
        selectedValues={
          record.inputState === InputState.ENTERED ? (record.values.reanimationZusaetzeTod?.options ?? []) : []
        }
        onValuesChange={updateDidDie}
      />
      <div className='symptoms-list indented-checkbox-parent'>
        <RadioList
          items={determinationRadioItems}
          name='death-group'
          value={
            record.inputState === InputState.ENTERED ? (record.values.reanimationZusaetzeTodArt?.option ?? null) : null
          }
          onChange={updateKind}
        />
      </div>
      <div className='small-spacer' />
      <Box textAlign='center' className='centered time-of-death'>
        <TimePicker label='Todeszeitpunkt' value={timeString} onChange={updateTime} />
      </Box>
      <div className='symptoms-list'>
        <CheckboxList
          items={timeOfDeathItem}
          selectedValues={
            record.inputState === InputState.ENTERED
              ? (record.values.reanimationZusaetzeTodeszeitpunktUnklar?.options ?? [])
              : []
          }
          onValuesChange={updateUnknownTime}
        />
      </div>
    </DiviFieldCard>
  );
}
